import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
    Gift,
    Copy,
    Check,
    WhatsappLogo,
    LinkedinLogo,
    XLogo,
    EnvelopeSimple,
    InstagramLogo,
    FacebookLogo,
    ThreadsLogo,
    ArrowRight,
    Users,
    Handshake,
    Coins,
    Download,
    TextAa,
} from "@phosphor-icons/react";
import ReferralPoster from "@/components/ReferralPoster";

const CURRENCY_SYMBOL = { INR: "₹", USD: "$", EUR: "€", GBP: "£" };

// "Refer a friend, earn a bounty" widget. Used on the landing page AND inside
// the authenticated candidate portal. When the current user is a candidate we
// wire in their personal referral code + share links; visitors see the value
// prop and a "Get your code" CTA that routes to candidate signup.
export default function ReferAndEarn({ variant = "landing" }) {
    const { user } = useAuth();
    const [bounty, setBounty] = useState({ amount: 1000, currency: "INR" });
    const [myCode, setMyCode] = useState(null);
    const [counts, setCounts] = useState(null);
    const [copied, setCopied] = useState(false);
    const [textCopied, setTextCopied] = useState(false);
    const [posterOpen, setPosterOpen] = useState(false);

    useEffect(() => {
        // Public bounty amount — safe to fetch without auth
        api
            .get("/public/referral/settings")
            .then((r) => setBounty(r.data))
            .catch(() => {});
    }, []);

    useEffect(() => {
        if (user?.role !== "candidate") return;
        api
            .get("/me/candidate/referrals")
            .then((r) => {
                setMyCode(r.data.referral_code);
                setCounts(r.data.counts || null);
            })
            .catch(() => {});
    }, [user]);

    const symbol = CURRENCY_SYMBOL[bounty.currency] || `${bounty.currency} `;
    const amount = Number(bounty.amount || 0).toLocaleString("en-IN");
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    // Shareable link goes through /api/r/{code} so social crawlers
    // (WhatsApp, LinkedIn, X, Slack, iMessage) get a rich preview card
    // with the referrer's name + bounty. Real browsers 302 back to signup.
    const shareLink = myCode
        ? `${origin}/api/r/${myCode}`
        : `${origin}/candidate/signup`;

    // V1.5.8.58 — hashtags on the referral share text, matching the
    // ShareJobModal style. LinkedIn / X / Threads / WhatsApp captions
    // render these as clickable tags; feeds surface them to broader
    // audiences. Capped at 5 (LinkedIn's soft ceiling).
    const referHashtags = [
        "#ReferAndEarn",
        "#NaukriDaddy",
        "#JobSearch",
        "#Hiring",
        "#CareerBoost",
    ].join(" ");
    const shareMsg = myCode
        ? `💼 I'm on NaukriDaddy — India's most curated agency job board.\n🎁 Sign up with my code ${myCode} and if you get hired I earn a ${symbol}${amount} bounty (no cap, no cool-off).\n\nJoin: ${shareLink}?src=REPLACE\n\n${referHashtags}`
        : `💼 NaukriDaddy is India's most curated agency job board.\n🎁 Refer a friend who gets hired and earn ${symbol}${amount} — no cap, no cool-off.\n\nJoin: ${shareLink}?src=REPLACE\n\n${referHashtags}`;
    // Small helper to inject the per-channel src attribution into the
    // shared message so click analytics can distinguish traffic sources.
    const msgFor = (src) => shareMsg.replace("REPLACE", src);

    // LinkedIn & Facebook strip the caption text from share intents — we
    // pre-copy the rich message + hashtags to the clipboard so the user
    // can paste with one Cmd/Ctrl+V once the composer opens.
    const copyMessageForPaste = async (src) => {
        const msg = msgFor(src);
        try {
            if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(msg);
                toast.success("Message + hashtags copied — paste with Cmd/Ctrl+V");
                return;
            }
        } catch (_) { /* fall through to textarea path */ }
        try {
            const ta = document.createElement("textarea");
            ta.value = msg;
            ta.style.position = "fixed";
            ta.style.opacity = "0";
            document.body.appendChild(ta);
            ta.focus();
            ta.select();
            document.execCommand("copy");
            document.body.removeChild(ta);
            toast.success("Message copied — paste with Cmd/Ctrl+V");
        } catch (_) { /* silent — link still opens */ }
    };

    // Try to open in a new tab; if the parent iframe or popup blocker
    // suppresses window.open we fall back to navigating the top-level
    // browsing context so the click at least *goes somewhere*.
    //
    // NOTE: window.open(url, "_blank", "noopener") *always* returns null
    // in modern browsers (noopener detaches the reference). Never treat
    // null as "popup blocked" outside an iframe or we'd wrongly navigate
    // the current tab too.
    const navigate = (url) => {
        const inIframe = (() => {
            try { return window.top !== window.self; } catch (_) { return true; }
        })();
        try {
            const w = window.open(url, "_blank", "noopener,noreferrer");
            if (!inIframe) return;
            if (w) return;
        } catch (_) { /* fall through */ }
        try {
            window.top.location.href = url;
        } catch (_) {
            window.location.href = url;
        }
    };

    const copyLink = async () => {
        const url = `${shareLink}?src=copy`;
        try {
            if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(url);
            } else {
                const ta = document.createElement("textarea");
                ta.value = url;
                document.body.appendChild(ta);
                ta.select();
                document.execCommand("copy");
                document.body.removeChild(ta);
            }
            setCopied(true);
            setTimeout(() => setCopied(false), 1800);
            toast.success("Link copied — paste it anywhere.");
        } catch {
            toast.error("Couldn't copy — long-press the link to copy manually.");
        }
    };

    // V1.5.9 — Explicit "Copy post text" flow for the referral widget.
    // LinkedIn / Facebook / Instagram strip caption params, so this
    // button always gives the user the exact hashtag-rich message in
    // their clipboard, ready to paste in any composer.
    const postText = msgFor("share");
    const copyPostText = async () => {
        let ok = false;
        try {
            if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(postText);
                ok = true;
            }
        } catch (_) { /* fall through */ }
        if (!ok) {
            try {
                const ta = document.createElement("textarea");
                ta.value = postText;
                ta.style.position = "fixed";
                ta.style.opacity = "0";
                document.body.appendChild(ta);
                ta.focus();
                ta.select();
                ok = document.execCommand("copy");
                document.body.removeChild(ta);
            } catch (_) { /* ignore */ }
        }
        if (ok) {
            setTextCopied(true);
            setTimeout(() => setTextCopied(false), 1800);
            toast.success("Post text + hashtags copied — paste with Cmd/Ctrl+V");
        } else {
            toast.error("Couldn't copy — long-press the text and copy manually.");
        }
    };

    // Prebuilt shareable URLs per channel. We render these as real anchor
    // tags with target="_blank" — programmatic window.open() gets blocked
    // by popup blockers and iframe embedders (e.g. preview environments)
    // whereas a plain <a target="_blank"> is a legitimate user-gesture nav.
    // Instagram has no public share-intent URL, so we copy the link + toast.
    const channels = [
        {
            id: "whatsapp",
            label: "WhatsApp",
            icon: WhatsappLogo,
            colorCls: "bg-[#25D366] hover:bg-[#1DA851]",
            href: `https://wa.me/?text=${encodeURIComponent(msgFor("whatsapp"))}`,
        },
        {
            id: "linkedin",
            label: "LinkedIn",
            icon: LinkedinLogo,
            colorCls: "bg-[#0A66C2] hover:bg-[#084d94]",
            href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareLink + "?src=linkedin")}`,
            // LinkedIn strips the caption from share-intent — pre-copy
            // the hashtag-rich message so the user pastes it in.
            beforeOpen: () => copyMessageForPaste("linkedin"),
        },
        {
            id: "facebook",
            label: "Facebook",
            icon: FacebookLogo,
            colorCls: "bg-[#1877F2] hover:bg-[#0e5fc1]",
            href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareLink + "?src=facebook")}`,
            beforeOpen: () => copyMessageForPaste("facebook"),
        },
        {
            id: "twitter",
            label: "X / Twitter",
            icon: XLogo,
            colorCls: "bg-black hover:bg-[#333]",
            href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(msgFor("twitter"))}`,
        },
        {
            id: "threads",
            label: "Threads",
            icon: ThreadsLogo,
            colorCls: "bg-[#0A0A0A] hover:bg-[#222]",
            href: `https://www.threads.net/intent/post?text=${encodeURIComponent(msgFor("threads"))}`,
        },
        {
            id: "instagram",
            label: "Instagram",
            icon: InstagramLogo,
            colorCls: "bg-gradient-to-tr from-[#F58529] via-[#DD2A7B] to-[#8134AF] hover:opacity-90",
            // Instagram has no public share-intent URL. Copy the caption
            // to clipboard and toast — the user pastes it into their
            // Story / Bio / DM manually. We deliberately DO NOT try to
            // launch `instagram://` (Chrome blocks it as "not secure").
            onClick: async () => {
                await copyMessageForPaste("instagram");
                toast.success(
                    "Referral message + hashtags copied. Open Instagram and paste it into your Story, Bio, or DM.",
                    { duration: 5500 },
                );
            },
        },
        {
            id: "email",
            label: "Email",
            icon: EnvelopeSimple,
            colorCls: "bg-cobalt hover:bg-cobalt-dark",
            href: `mailto:?subject=${encodeURIComponent("Join me on NaukriDaddy")}&body=${encodeURIComponent(msgFor("email"))}`,
        },
    ];

    return (
        <div
            data-testid="refer-and-earn"
            className={
                variant === "landing"
                    ? "bg-ink text-white"
                    : "bg-white border border-[#E5E5E5]"
            }
        >
            <div
                className={
                    variant === "landing"
                        ? "max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-20"
                        : "p-5 sm:p-6"
                }
            >
                <div
                    className={
                        variant === "landing"
                            ? "grid gap-8 lg:grid-cols-[1.1fr_1fr] items-start"
                            : "grid gap-6"
                    }
                >
                    {/* Left: value prop */}
                    <div>
                        <p
                            className={`text-[10px] font-black uppercase tracking-[0.25em] mb-3 inline-flex items-center gap-1.5 ${
                                variant === "landing" ? "text-splash" : "text-cobalt"
                            }`}
                        >
                            <Gift size={11} weight="fill" /> Refer &amp; earn
                        </p>
                        <h2
                            className={`font-heading font-bold tracking-tight ${
                                variant === "landing"
                                    ? "text-3xl sm:text-5xl"
                                    : "text-lg sm:text-xl"
                            }`}
                        >
                            Refer a friend.
                            <br className="hidden sm:inline" />{" "}
                            <span
                                className={
                                    variant === "landing" ? "text-splash" : "text-cobalt"
                                }
                            >
                                Earn {symbol}
                                {amount}
                            </span>{" "}
                            when they&apos;re hired.
                        </h2>
                        <p
                            className={`mt-4 text-sm max-w-lg ${
                                variant === "landing" ? "text-white/70" : "text-[#525252]"
                            }`}
                        >
                            Share your personal invite link. When your friend joins
                            NaukriDaddy and lands a role through us, you pocket a
                            {" "}{symbol}{amount} bounty — no cap, no cool-off period.
                        </p>

                        {/* How it works — 3 tiny steps */}
                        <div className="mt-6 grid gap-3 sm:grid-cols-3 max-w-lg">
                            {[
                                { icon: Gift, label: "Share your link" },
                                { icon: Handshake, label: "Friend gets hired" },
                                { icon: Coins, label: `You earn ${symbol}${amount}` },
                            ].map(({ icon: Icon, label }) => (
                                <div
                                    key={label}
                                    className={`flex items-center gap-2 text-xs ${
                                        variant === "landing" ? "text-white/85" : "text-[#262626]"
                                    }`}
                                >
                                    <Icon
                                        size={16}
                                        weight="bold"
                                        className={
                                            variant === "landing" ? "text-splash" : "text-cobalt"
                                        }
                                    />
                                    <span>{label}</span>
                                </div>
                            ))}
                        </div>

                        {/* Optional live stats for logged-in candidates */}
                        {myCode && counts && (
                            <div
                                data-testid="refer-live-stats"
                                className="mt-6 flex flex-wrap gap-6 text-xs text-white/70"
                            >
                                <div>
                                    <p className="text-[9px] font-black uppercase tracking-[0.2em] text-white/50">
                                        Signups
                                    </p>
                                    <p className="font-heading text-2xl font-black text-white">
                                        {counts.signups || 0}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-[9px] font-black uppercase tracking-[0.2em] text-white/50">
                                        Interviewing
                                    </p>
                                    <p className="font-heading text-2xl font-black text-white">
                                        {counts.interviewing || 0}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-[9px] font-black uppercase tracking-[0.2em] text-white/50">
                                        Hired
                                    </p>
                                    <p className="font-heading text-2xl font-black text-splash">
                                        {counts.hired || 0}
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Right: share card */}
                    <div
                        className={
                            variant === "landing"
                                ? "bg-white text-ink p-5 sm:p-6"
                                : "bg-[#FAFAFA] p-5"
                        }
                    >
                        {myCode ? (
                            <>
                                <p className="text-[10px] font-black uppercase tracking-[0.25em] text-cobalt mb-2">
                                    Your referral code
                                </p>
                                <div
                                    data-testid="refer-my-code"
                                    className="font-heading text-3xl sm:text-4xl font-black tracking-tighter mb-4"
                                >
                                    {myCode}
                                </div>
                                <div className="flex items-center gap-2 mb-5 bg-[#F5F5F5] border border-[#E5E5E5] px-3 py-2">
                                    <p className="text-xs text-[#525252] truncate flex-1" title={shareLink}>
                                        {shareLink}
                                    </p>
                                    <button
                                        onClick={copyLink}
                                        data-testid="refer-copy-link"
                                        className="text-cobalt hover:text-cobalt-dark shrink-0"
                                        aria-label="Copy referral link"
                                    >
                                        {copied ? (
                                            <Check size={16} weight="bold" />
                                        ) : (
                                            <Copy size={16} weight="bold" />
                                        )}
                                    </button>
                                </div>
                            </>
                        ) : (
                            <>
                                <p className="text-[10px] font-black uppercase tracking-[0.25em] text-cobalt mb-2">
                                    Get your code
                                </p>
                                <p className="font-heading text-lg sm:text-xl font-bold mb-4">
                                    Create a free candidate account to unlock your personal
                                    referral link.
                                </p>
                                <Link
                                    to="/candidate/signup"
                                    data-testid="refer-signup-cta"
                                    className="btn-gradient inline-flex items-center gap-2 px-5 py-3 text-[11px] font-black uppercase tracking-[0.2em] mb-5"
                                >
                                    Get my referral code
                                    <ArrowRight size={12} weight="bold" />
                                </Link>
                            </>
                        )}

                        {/* V1.5.9 — Visible "post text" panel with an
                            explicit Copy button. LinkedIn / Facebook /
                            Instagram all strip caption params from their
                            share intents — this always-visible textarea
                            + Copy button guarantees the user has the
                            hashtag-rich message in their clipboard,
                            ready to paste in any composer. */}
                        <div data-testid="refer-post-text-panel" className="border border-[#E5E5E5] bg-white mb-4">
                            <div className="flex items-center justify-between px-3 py-2 bg-[#F5F5F5] border-b border-[#E5E5E5]">
                                <div className="flex items-center gap-1.5">
                                    <TextAa size={12} weight="bold" className="text-[#525252]" />
                                    <span className="text-[10px] font-black uppercase tracking-wider text-[#525252]">
                                        Your post text
                                    </span>
                                </div>
                                <button
                                    type="button"
                                    onClick={copyPostText}
                                    data-testid="refer-copy-post-text"
                                    className={`px-3 py-1 text-[10px] font-black uppercase tracking-wider inline-flex items-center gap-1.5 transition-colors ${
                                        textCopied
                                            ? "bg-emerald-500 text-white"
                                            : "bg-ink hover:bg-black text-white"
                                    }`}
                                >
                                    {textCopied ? (
                                        <><Check size={11} weight="bold" /> Copied</>
                                    ) : (
                                        <><Copy size={11} weight="bold" /> Copy post text</>
                                    )}
                                </button>
                            </div>
                            <textarea
                                readOnly
                                value={postText}
                                data-testid="refer-post-text-body"
                                onFocus={(e) => e.target.select()}
                                rows={6}
                                className="w-full px-3 py-2 text-xs text-ink font-mono leading-relaxed resize-none outline-none bg-white"
                            />
                            <p className="text-[10px] text-[#525252] px-3 py-2 border-t border-[#F5F5F5] bg-[#FAFAFA] leading-relaxed">
                                <strong>Tip:</strong> LinkedIn &amp; Facebook don't allow pre-filled captions.
                                Tap <strong>Copy post text</strong> above, click LinkedIn/Facebook below, then paste (Cmd/Ctrl+V) into their composer.
                            </p>
                        </div>

                        <p className="text-[10px] font-black uppercase tracking-[0.25em] text-[#525252] mb-2">
                            Share to
                        </p>
                        <div
                            data-testid="refer-share-buttons"
                            className="grid grid-cols-5 gap-2"
                        >
                            {channels.map(({ id, label, icon: Icon, colorCls, href, onClick, beforeOpen }) => {
                                // V1.5.8.56 — every share button always works,
                                // matching the job-description Share modal.
                                // Without a referral code, the shared link is
                                // the generic signup URL — friends can still
                                // land on the site, and the sharer is nudged
                                // (via the "Get your code" CTA above) to sign
                                // up so bounty attribution kicks in on
                                // subsequent shares.
                                const cls = `aspect-square flex items-center justify-center text-white transition-opacity ${colorCls}`;
                                if (onClick) {
                                    return (
                                        <button
                                            key={id}
                                            type="button"
                                            data-testid={`refer-share-${id}`}
                                            onClick={onClick}
                                            className={cls}
                                            title={label}
                                            aria-label={`Share on ${label}`}
                                        >
                                            <Icon size={20} weight="bold" />
                                        </button>
                                    );
                                }
                                return (
                                    <a
                                        key={id}
                                        href={href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid={`refer-share-${id}`}
                                        className={cls}
                                        title={label}
                                        aria-label={`Share on ${label}`}
                                        onClick={(e) => {
                                            // Iframe preview envs silently block
                                            // target=_blank; navigate() falls back
                                            // to window.top.location so the click
                                            // always "goes somewhere".
                                            e.preventDefault();
                                            // LinkedIn / Facebook strip the caption
                                            // from their share-intent URLs — pre-
                                            // copy the hashtag-rich message so the
                                            // user pastes it in the composer.
                                            if (beforeOpen) { beforeOpen(); }
                                            navigate(href);
                                        }}
                                    >
                                        <Icon size={20} weight="bold" />
                                    </a>
                                );
                            })}
                        </div>
                        {!myCode && (
                            <p className="text-[10px] text-[#A3A3A3] mt-3">
                                Sharing works right now — but sign up first to attach
                                your referral code and earn ₹5,000 when your friend is
                                hired.
                            </p>
                        )}
                        {myCode && (
                            <button
                                type="button"
                                onClick={() => setPosterOpen(true)}
                                data-testid="refer-download-poster"
                                className="mt-4 w-full border border-[#E5E5E5] hover:border-cobalt hover:text-cobalt text-ink px-4 py-2.5 text-[11px] font-black uppercase tracking-[0.2em] inline-flex items-center justify-center gap-2 transition-colors"
                            >
                                <Download size={12} weight="bold" /> Download poster (Instagram/WhatsApp)
                            </button>
                        )}
                    </div>
                </div>
            </div>
            {/* Off-modal poster generator */}
            {myCode && (
                <ReferralPoster
                    open={posterOpen}
                    onClose={() => setPosterOpen(false)}
                    referralCode={myCode}
                    referrerName={user?.name}
                    bountyLabel={`${symbol}${amount}`}
                    shareUrl={shareLink}
                />
            )}
        </div>
    );
}
